import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import useApi from "hooks/useApi";
import { CallStatus } from "types/auth";
import useFirebase from "hooks/useFirebase";
import useSession from "hooks/call-queue/useSession";
import VideoCallPlaceholder from "./VideoCallPlaceholder";
import VideoCall from "./VideoCall";

// Statuses in which this page can render the live call UI. CALLING/RECEIVING are
// included so the outgoing caller sees their own call ("Connecting…" + End Call)
// while the app is still ringing, instead of the placeholder.
const RENDERABLE = [
    CallStatus.CALLING,
    CallStatus.RECEIVING,
    CallStatus.RECEIVED,
    CallStatus.IN_PROGRESS,
    CallStatus.COMPLETED,
    CallStatus.USER_LEFT
];

const VideoSessionPage = () => {
    const { sessionId } = useParams();
    const { answerCall, user } = useApi();
    const { session, setCallUid } = useSession();
    const { updateCallStatus, addCallHistoryEntry, updateSessionData, getCallData } = useFirebase();
    const [isInitializing, setIsInitializing] = useState(true);

    // Answering is not idempotent: the backend mints a BRAND NEW Agora session
    // (fresh tokens for every participant) and the call flips to RECEIVED. Doing
    // that when we are already a participant is what broke two flows:
    //
    //   - dashboard → app calls: navigating here right after initiating made the
    //     dashboard "answer" its own outgoing call, so the phone's CallKit UI was
    //     torn down about a second into the ring;
    //   - refresh mid-call: the reload re-answered a live call and replaced the
    //     session both sides were already joined to, so the call never came back.
    //
    // Holding a token under `session.tokens` is exactly "we are already in this
    // call" — an app-initiated call carries no session node at all until support
    // answers it, so a genuine incoming call never matches.
    const alreadyParticipant = (call: any) =>
        Array.isArray(call?.session?.tokens) &&
        call.session.tokens.some((t: any) => t?.uid === user?.uid);

    const answerCallAndGetSessionInfo = async () => {
        try {
            setIsInitializing(true);

            // Read the call node first. `session` from the context listener is still
            // null on the first render, so it cannot be used to make this decision.
            const call = await getCallData(sessionId!);
            if (alreadyParticipant(call)) {
                console.log("Already a participant in this call, skipping answer");
                setIsInitializing(false);
                return;
            }

            let sessionData = await answerCall(sessionId!, CallStatus.RECEIVED);
            let { success, data } = sessionData;
            if(success){
                updateCallStatus(sessionId!, CallStatus.RECEIVED);
                addCallHistoryEntry(sessionId!, { status: CallStatus.RECEIVED, datetime: new Date().toISOString(), user: user.uid })
                updateSessionData(sessionId!, data);
            }
            setIsInitializing(false);
        } catch (error) {
            console.error("Error initializing session:", error);
            setIsInitializing(false);
        }
    }

    useEffect(() => {
        if(sessionId){
            setCallUid(sessionId);
            answerCallAndGetSessionInfo();
        }
    }, [sessionId])

    const renderable = !!session && RENDERABLE.includes(session.status);

    return (
        <>
            {(isInitializing || !renderable) && <VideoCallPlaceholder />}
            {!isInitializing && renderable && <VideoCall />}
        </>
    )
}

export default VideoSessionPage;
